import { useDebounceFn } from '@vueuse/core';
import { GaiaEventType, WebsocketSubscriptionTarget, type GaiaEvent } from '@/api';
import { getWsClient, type WebSocketEventHandler } from '@/services/WebSocketService';
import { WEBSOCKET_DISABLED_KEY } from '@/composables/useReviewWebSocket';
import { onBeforeUnmount, onMounted, watch } from 'vue';
import { logger } from '@/utils/logger';

export interface UsePlaybookRunWebSocketOptions {
  /** Run uids to keep subscribed. Reconciled live — subscriptions follow this set. */
  runUids: () => string[];
  /** A `playbook_run_updated` arrived for a watched run — refetch it. */
  onRunUpdated: (uid: string) => void;
  /** Optional: also subscribe to the playbook channel for run-created events. */
  playbookUid?: () => string | null;
  /** A `playbook_run_created` arrived for the watched playbook — refetch the list. */
  onRunCreated?: () => void;
}

function eventPayloadUid(event: GaiaEvent): string | null {
  const data = event.data;
  if (typeof data !== 'object' || !('uid' in data)) {
    return null;
  }
  const uid = (data as { uid?: unknown }).uid;
  return typeof uid === 'string' ? uid : null;
}

/**
 * Keep one or more playbook runs in sync with the backend's `playbook_run_updated`
 * websocket events. The single shared socket fans every subscribed event out to
 * every mounted caller, so each filters incoming events against its own
 * subscribed `runUids` before refetching.
 *
 * The detail view watches a single run; the runs table watches the current
 * page's runs. Subscriptions are refcounted in the service, so an overlapping
 * uid across views is safe.
 */
export function usePlaybookRunWebSocket(options: UsePlaybookRunWebSocketOptions): void {
  const subs = new Map<string, () => void>();
  const disposers: (() => void)[] = [];

  function reconcile(desired: string[]): void {
    const wsClient = getWsClient();
    const desiredSet = new Set(desired.filter(Boolean));

    for (const [uid, dispose] of subs) {
      if (!desiredSet.has(uid)) {
        dispose();
        subs.delete(uid);
      }
    }
    for (const uid of desiredSet) {
      if (!subs.has(uid)) {
        subs.set(uid, wsClient.addSubscriptionUid(WebsocketSubscriptionTarget.PLAYBOOK_RUN, uid));
      }
    }
  }

  const handler: WebSocketEventHandler = event => {
    if (event.gaia_event_type !== GaiaEventType.PLAYBOOK_RUN_UPDATED) {
      return;
    }
    const uid = eventPayloadUid(event);
    if (!uid || !subs.has(uid)) {
      return;
    }
    options.onRunUpdated(uid);
  };

  // Chat or drag-drop can create many runs at once; collapse the burst into one refetch.
  const debouncedRunCreated = useDebounceFn(() => options.onRunCreated?.(), 500);
  let subscribedPlaybookUid: string | null = null;
  let disposePlaybookSub: (() => void) | null = null;

  function eventPayloadPlaybookUid(event: GaiaEvent): string | null {
    const data = event.data;
    if (typeof data !== 'object' || !('data' in data)) {
      return null;
    }
    const inner = (data as { data?: unknown }).data;
    if (typeof inner !== 'object' || inner === null) {
      return null;
    }
    const playbookUid = (inner as { playbook_uid?: unknown }).playbook_uid;
    return typeof playbookUid === 'string' ? playbookUid : null;
  }

  const createdHandler: WebSocketEventHandler = event => {
    if (event.gaia_event_type !== GaiaEventType.PLAYBOOK_RUN_CREATED) {
      return;
    }
    if (!subscribedPlaybookUid) {
      return;
    }
    // The payload's top-level uid is the run uid (unknown to us pre-fetch), so we can
    // only attribute the event when the payload names a playbook. Fail open otherwise:
    // the server only pushes events for subscribed uids, so the worst case is a
    // spurious refetch when another mounted view watches a different playbook.
    const playbookUid = eventPayloadPlaybookUid(event);
    if (playbookUid && playbookUid !== subscribedPlaybookUid) {
      return;
    }
    void debouncedRunCreated();
  };

  function reconcilePlaybook(uid: string | null): void {
    if (uid === subscribedPlaybookUid) {
      return;
    }
    disposePlaybookSub?.();
    disposePlaybookSub = null;
    subscribedPlaybookUid = uid;
    if (uid) {
      disposePlaybookSub = getWsClient().addSubscriptionUid(
        WebsocketSubscriptionTarget.PLAYBOOK,
        uid
      );
    }
  }

  onMounted(() => {
    if (localStorage.getItem(WEBSOCKET_DISABLED_KEY) === 'true') {
      logger.warn('[WebSocket] WebSocket is disabled via settings');
      return;
    }

    const wsClient = getWsClient();
    disposers.push(
      wsClient.registerHandler(WebsocketSubscriptionTarget.PLAYBOOK_RUN, handler),
      watch(
        options.runUids,
        uids => {
          reconcile(uids);
        },
        { immediate: true }
      )
    );

    if (options.playbookUid && options.onRunCreated) {
      disposers.push(
        wsClient.registerHandler(WebsocketSubscriptionTarget.PLAYBOOK, createdHandler),
        watch(
          options.playbookUid,
          uid => {
            reconcilePlaybook(uid);
          },
          { immediate: true }
        )
      );
    }
  });

  onBeforeUnmount(() => {
    for (const dispose of subs.values()) {
      dispose();
    }
    subs.clear();
    disposePlaybookSub?.();
    disposePlaybookSub = null;
    subscribedPlaybookUid = null;
    for (const dispose of disposers) {
      dispose();
    }
    disposers.length = 0;
  });
}
